import React, {useEffect} from 'react';
import {format, parseISO} from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import Loading from '../Loading/Loading';
import {ErrorPage} from '../../pages/ErrorPage';
import {useHistoryDiff, HistoryQueryType} from './useHistoryDiff';
import {OVModelsEnum} from '../../schema-types/globalTypes';
import {HistoryDiffDetails} from '../../schema-types/HistoryDiffDetails';

type DialogProps = {
  isOpen: boolean;
  handleClose: () => void;
};

type HistoryDiffProps = {
  queryType: HistoryQueryType;
  entityId?: string | null;
  entityType?: OVModelsEnum;
  staffId?: string | null;
  daysSince?: number | null;
  setGroupHistoryLoading?: (loading: boolean) => void;
  hideRecord?: boolean;
};

export function HistoryDiff({
  queryType,
  entityId,
  entityType,
  staffId,
  daysSince,
  setGroupHistoryLoading,
  hideRecord = false,
}: HistoryDiffProps) {
  const {onGetHistoryDiff, data, loading, error} = useHistoryDiff(queryType);

  useEffect(() => {
    const variables =
      queryType === HistoryQueryType.GENERIC
        ? {entityId, entityType}
        : queryType === HistoryQueryType.STAFF
        ? {staffId}
        : {daysSince};

    if (Object.values(variables).some((value) => value == null)) {
      console.error('Missing required parameter:', variables);
    } else {
      onGetHistoryDiff({variables});
    }
  }, [onGetHistoryDiff, entityId, entityType, staffId, daysSince, queryType]);

  useEffect(() => {
    if (queryType === HistoryQueryType.GROUP && setGroupHistoryLoading) {
      setGroupHistoryLoading(loading);
    }
  }, [onGetHistoryDiff, entityId, entityType, staffId, daysSince, queryType]);

  useEffect(() => {
    if (queryType === HistoryQueryType.GROUP && setGroupHistoryLoading) {
      setGroupHistoryLoading(loading);
    }
  }, [loading, queryType, setGroupHistoryLoading]);

  if (loading) return <Loading />;
  if (
    (queryType === HistoryQueryType.GENERIC && !entityId) ||
    (queryType === HistoryQueryType.GROUP && daysSince === undefined) ||
    (queryType === HistoryQueryType.STAFF && !staffId)
  ) {
    console.error('Missing parameter:', {queryType, entityId, staffId, daysSince});
    return <ErrorPage code="404" text="No ID or required parameter provided" />;
  }
  if (error) {
    return <ErrorPage code="500" text="There was an issue loading Review History" />;
  }

  const groupedByDate = data?.reduce<Record<string, HistoryDiffDetails[]>>(
    (acc, item) => {
      const date = item.date || 'Unknown Date';
      acc[date] = acc[date] || [];
      acc[date].push(item);
      return acc;
    },
    {}
  );

  return (
    <>
      {data && data.length === 0 ? (
        <Typography variant="body1" sx={{textAlign: 'center', mt: 2}}>
          No change histoy data yet exists.
        </Typography>
      ) : (
        groupedByDate &&
        Object.entries(groupedByDate).map(([date, records]) => {
          const formattedDate = date
            ? format(parseISO(date), "EEEE do MMMM yyyy 'at' HH:mm")
            : 'Unknown Date';
          const primaryUser = records[0].user || 'Unknown User';
          return (
            <TableContainer component={Paper} key={date} sx={{mb: 2}}>
              <Paper
                sx={{
                  borderTop: '3px solid rgb(95, 200, 235)',
                  p: 1,
                  backgroundColor: '#f5f5f5',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{textAlign: 'center'}}
                >{`Changes made by ${primaryUser} on ${formattedDate}`}</Typography>
              </Paper>
              <Table aria-label="history table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{width: '16%'}}>Field</TableCell>
                    <TableCell sx={{width: '16%'}}>Old Value</TableCell>
                    <TableCell sx={{width: '16%'}}>New Value</TableCell>
                    <TableCell sx={{width: '16%'}}>Reason</TableCell>
                    {hideRecord ? null : (
                      <TableCell sx={{width: '16%'}}>Record</TableCell>
                    )}
                    <TableCell sx={{width: '16%'}}>Extra</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={`${record.field}-${record.date}-${record.newValue}`}>
                      <TableCell>{record.field}</TableCell>
                      <TableCell>{record.oldValue}</TableCell>
                      <TableCell>{record.newValue}</TableCell>
                      <TableCell>{record.reason}</TableCell>
                      {hideRecord ? null : <TableCell>{record.record}</TableCell>}
                      <TableCell>{record.extra}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })
      )}
    </>
  );
}

function HistoryDiffDialog({
  isOpen,
  handleClose,
  ...props
}: HistoryDiffProps & DialogProps) {
  if (!isOpen) {
    return null;
  }
  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="history-dialog"
      aria-describedby="history-dialog-description"
    >
      <DialogTitle id="history-dialog-title">History</DialogTitle>
      <DialogContent>
        <HistoryDiff {...props} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default HistoryDiffDialog;
